import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Tippy from '@tippyjs/react';
import Popup from 'reactjs-popup';

import UserMenu from '../../UserMenu/components/UserMenu';
import { Conductor } from '@opendate-app/setlist';
import useConductorWidgetHint from '../useConductorWidgetHint';
import ConductorWidgetHint from './ConductorWidgetHint';
import NavMenuIcon from '../../shared/Icons/NavMenuIcon';
import UnreadDot from '../../shared/components/UnreadDot';
import OpendateLogo from '../../shared/Icons/OpendateLogo';
import OnboardingNavItem from './OnboardingNavItem';
import UniversalSearch from '../../shared/components/UniversalSearch/UniversalSearch';

const popupContentStyle = {
  background: 'var(--sl-bg-primary-alt)',
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: '12px',
  boxShadow: 'var(--sl-shadow-lg)',
  width: '320px',
  padding: '0px',
  overflowX: 'hidden',
  overflowY: 'auto',
  maxHeight: 'calc(100vh - 24px)',
  // Above the collapsed-rail hover tray (.nav-v2-tray, z-index 40), which is a
  // sibling in the same sidebar stacking context and would otherwise cover it.
  zIndex: '50',
};

// Double-arrow toggle icon — same glyph as the collapsed-state expand button,
// so collapse and expand read as the same control.
const CollapseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M13.3333 11.3333H2.66666M2.66666 11.3333L5.33332 14M2.66666 11.3333L5.33332 8.66667M13.3333 4.66667H5.99999M5.99999 4.66667L8.66666 7.33333M5.99999 4.66667L8.66666 2" stroke="#A4A7AE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ponytail: down-chevron; the .up CSS class flips it 180° on open (clean flip, not a spin).
const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

// Left chevron in the tray header — collapses the tray back to the rail.
const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

// Full-height slide-out sub-nav for the collapsed rail. Opens on hover (see openTray),
// closes when the pointer leaves the whole sidebar or the header chevron is clicked.
const NavTray = ({ label, items, onCollapse }) => (
  <div className="nav-v2-tray">
    <div className="nav-v2-tray-head">
      <span className="nav-v2-tray-title">{label}</span>
      <button type="button" className="nav-v2-tray-collapse" onClick={onCollapse} aria-label="Collapse">
        <ChevronLeftIcon />
      </button>
    </div>
    <div className="nav-v2-tray-nav">
      {items.map((i) => (
        <a key={i.key} href={i.href} aria-label={i.toolTip}
           className={'nav-v2-tray-item d-flex align-items-center' + (i.active ? ' active' : '')}>
          <NavMenuIcon xlinkHref={i.xlinkHref} />
          <span className="menu-label">{i.toolTip}</span>
          {i.badge != null && <span className="nav-v2-badge">{i.badge}</span>}
        </a>
      ))}
    </div>
  </div>
);

const NavLink = ({ item, isExpanded, hideIcon }) => (
  <Tippy placement="right" content={item.toolTip} disabled={isExpanded}>
    <div className={'d-flex align-items-center position-relative main-menu-link' + (item.active ? ' active' : '')}>
      <a href={item.href} className="stretched-link d-flex align-items-center" aria-label={item.toolTip}>
        {!hideIcon && <NavMenuIcon xlinkHref={item.xlinkHref} />}
        {isExpanded && <span className="menu-label">{item.toolTip}</span>}
        {isExpanded && item.badge != null && <span className="nav-v2-badge">{item.badge}</span>}
      </a>
    </div>
  </Tippy>
);

const AppMenuV2 = ({ navigationV2Menu, team, settingsLink, helpLink, myTicketsLink, isGuest, csrfToken, currentUser, teamMemberships, sidebarExpanded, persistSidebar = true, initialOpenGroups, initialClosedGroups, searchUrl, searchSections }) => {
  const { anchors, groups, footer } = navigationV2Menu;
  // Init from the server-computed prop (read from the sidebarExpanded cookie) so the
  // prerendered rail width matches this render — no expand-then-collapse flash.
  const [isExpanded, setIsExpanded] = useState(sidebarExpanded !== false);
  const [showConductorHint, hideConductorHint] = useConductorWidgetHint();
  // Resolve open groups from explicit user prefs (two cookies, via props) layered
  // over the default: the group owning the current page auto-opens, groups the user
  // opened stay open, groups the user closed stay closed — even the active one.
  // Prop-seeded (not a document.cookie read) to keep the prerender and first client
  // render identical — no hydration mismatch.
  const openedPref = useRef(new Set(initialOpenGroups));
  const closedPref = useRef(new Set(initialClosedGroups));
  const [openGroups, setOpenGroups] = useState(() => {
    const active = groups.filter((g) => g.items && g.items.some((i) => i.active)).map((g) => g.key);
    return Array.from(new Set([...active, ...initialOpenGroups]))
      .filter((k) => !closedPref.current.has(k));
  });
  const [trayGroup, setTrayGroup] = useState(null);
  const openTimer = useRef();
  const closeTimer = useRef();

  useEffect(() => {
    document.body.classList.add('nav-v2');
    return () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); };
  }, []);

  // Collapsed rail: hovering a rail icon slides its tray out after a short intent
  // delay; the tray stays open while the pointer is anywhere over the sidebar and
  // closes shortly after it leaves. No click required.
  const openTray = (key) => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setTrayGroup(key), 110);
  };
  const scheduleCloseTray = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setTrayGroup(null), 260);
  };
  const cancelCloseTray = () => clearTimeout(closeTimer.current);
  const closeTrayNow = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setTrayGroup(null);
  };

  const trayables = [
    ...anchors.filter((a) => a.subItems && a.subItems.length > 0)
      .map((a) => ({ key: a.key, label: a.toolTip, items: a.subItems })),
    ...groups.filter((g) => !g.standalone).map((g) => ({ key: g.key, label: g.label, items: g.items })),
    ...groups.filter((g) => g.standalone && g.subItems && g.subItems.length > 0)
      .map((g) => ({ key: g.key, label: g.toolTip, items: g.subItems })),
  ];
  const activeTray = !isExpanded && trayGroup ? trayables.find((t) => t.key === trayGroup) : null;

  useEffect(() => {
    document.body.classList.toggle('sidebar-expanded', isExpanded);
  }, [isExpanded]);

  const setExpanded = (val) => {
    setIsExpanded(val);
    if (!persistSidebar) return;
    // Cookie (not localStorage) so the server can read it and prerender the correct
    // width on the next page load. 1-year expiry.
    document.cookie = `sidebarExpanded=${val}; path=/; max-age=31536000; samesite=lax`;
  };

  const writeGroupCookie = (name, set) => {
    document.cookie = `${name}=${Array.from(set).join(',')}; path=/; max-age=31536000; samesite=lax`;
  };

  const toggleGroup = (key) => {
    const willOpen = !openGroups.includes(key);
    setOpenGroups(willOpen ? [...openGroups, key] : openGroups.filter((k) => k !== key));
    // Record the explicit intent on both cookies so opening a closed group and
    // closing the (force-open) active group each survive a refresh.
    const [add, remove] = willOpen ? [openedPref, closedPref] : [closedPref, openedPref];
    add.current.add(key);
    remove.current.delete(key);
    writeGroupCookie('navOpenGroups', openedPref.current);
    writeGroupCookie('navClosedGroups', closedPref.current);
  };

  // Expanded: click toggles the inline submenu. Collapsed: the tray opens on hover,
  // so click does nothing.
  const onGroupClick = (key) => { if (isExpanded) toggleGroup(key); };

  return (
    <div className={'card app-menu-sidebar' + (isExpanded ? ' expanded' : '')}
         onMouseEnter={isExpanded ? undefined : cancelCloseTray}
         onMouseLeave={isExpanded ? undefined : scheduleCloseTray}>
      <div className={'card-body d-flex flex-column' + (isExpanded ? ' align-items-stretch' : ' align-items-center')}>
        <div className="nav-v2-header d-flex align-items-center justify-content-between">
          {isExpanded ? (
            <React.Fragment>
              <OpendateLogo height={30} />
              <button type="button" className="sidebar-toggle-btn" onClick={() => setExpanded(false)} aria-label="Collapse navigation">
                <CollapseIcon />
              </button>
            </React.Fragment>
          ) : (
            <button type="button" className="sidebar-toggle-wrapper" onClick={() => setExpanded(true)} aria-label="Expand navigation">
              <OpendateLogo height={36} markOnly />
              <div className="sidebar-expand-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: 'rotate(180deg)' }}>
                  <path d="M13.3333 11.3333H2.66666M2.66666 11.3333L5.33332 14M2.66666 11.3333L5.33332 8.66667M13.3333 4.66667H5.99999M5.99999 4.66667L8.66666 7.33333M5.99999 4.66667L8.66666 2" stroke="#A4A7AE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </button>
          )}
        </div>

        {searchUrl && (
          <UniversalSearch searchUrl={searchUrl} sections={searchSections} collapsed={!isExpanded} onExpand={() => setExpanded(true)} />
        )}

        <div className="flex-grow-1 nav-v2-scroll">
          {anchors.map((a) => {
            const hasTray = !isExpanded && a.subItems && a.subItems.length > 0;
            return (
              <div key={a.key} className={'nav-v2-anchor' + (a.dividerAfter ? ' nav-v2-anchor-divided' : '')}
                   onMouseEnter={hasTray ? () => openTray(a.key) : undefined}>
                <NavLink item={a} isExpanded={isExpanded} />
                {isExpanded && a.subItems && a.subItems.map((s) => (
                  <div key={s.key} className="nav-v2-subitem">
                    <NavLink item={s} isExpanded={isExpanded} hideIcon />
                  </div>
                ))}
              </div>
            );
          })}

          {groups.map((g) => {
            // Standalone link (e.g. Reports) — a plain top-level nav item, not a collapsible group.
            if (g.standalone) {
              const hasTray = !isExpanded && g.subItems && g.subItems.length > 0;
              return (
                <div key={g.key} className="nav-v2-anchor"
                     onMouseEnter={hasTray ? () => openTray(g.key) : undefined}>
                  <NavLink item={g} isExpanded={isExpanded} />
                </div>
              );
            }
            const open = openGroups.includes(g.key);
            const groupActive = g.items && g.items.some((i) => i.active);
            return (
              <div key={g.key} className={'nav-v2-group' + (open ? ' open' : '') + (groupActive ? ' active' : '')}
                   onMouseEnter={isExpanded ? undefined : () => openTray(g.key)}>
                <Tippy placement="right" content={g.label} disabled={isExpanded}>
                  <button type="button" className={'nav-v2-group-header main-menu-link' + (groupActive ? ' active' : '')} onClick={() => onGroupClick(g.key)} aria-label={g.label}>
                    <span className="position-relative d-inline-flex">
                      <NavMenuIcon xlinkHref={g.icon} />
                      {g.unread && <UnreadDot label={g.unreadLabel} />}
                    </span>
                    {isExpanded && <span className="menu-label">{g.label}</span>}
                    {isExpanded && <span className={'nav-v2-chev' + (open ? ' up' : '')} aria-hidden="true"><ChevronIcon /></span>}
                  </button>
                </Tippy>
                {isExpanded && open && (
                  <div className="nav-v2-submenu">
                    {g.items.map((i) => (
                      <div key={i.key} className="nav-v2-subitem"><NavLink item={i} isExpanded={isExpanded} hideIcon /></div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* Tray lives inside the nav-item column so its height spans exactly the rail
              items (top of first → bottom of last), not the whole viewport. */}
          {activeTray && <NavTray label={activeTray.label} items={activeTray.items} onCollapse={closeTrayNow} />}
        </div>

        <div className="nav-v2-footer">
          {team.package && team.package.ai_features && !isGuest && (
            <Tippy key={showConductorHint ? 'hint' : 'label'}
                   placement="right"
                   {...(showConductorHint
                     ? { content: <ConductorWidgetHint onDismiss={hideConductorHint} />, visible: true, interactive: true, appendTo: () => document.body }
                     : { content: 'Conductor', disabled: isExpanded })}>
              <button type="button" className="main-menu-link d-flex align-items-center" data-conductor-launcher="true"
                   aria-label="Conductor"
                   onClick={() => {
                     hideConductorHint();
                     window.dispatchEvent(new CustomEvent('openConductor', { detail: { source: 'app_menu' } }));
                   }} style={{ cursor: 'pointer' }}>
                <Conductor className="conductor-nav-icon" width={20} height={20} />
                {isExpanded && <span className="menu-label">Conductor</span>}
              </button>
            </Tippy>
          )}
          <OnboardingNavItem item={navigationV2Menu.onboarding} teamId={team.id} isExpanded={isExpanded} />
          {footer.map((f) => <NavLink key={f.key} item={f} isExpanded={isExpanded} />)}
          {Object.keys(helpLink).length > 0 && <NavLink item={{ ...helpLink, key: 'help' }} isExpanded={isExpanded} />}
          {Object.keys(settingsLink).length > 0 && <NavLink item={{ ...settingsLink, key: 'settings' }} isExpanded={isExpanded} />}
          <div className="nav-v2-account">
            <Popup arrow={false}
                  offsetY={0}
                  offsetX={5}
                  position="right bottom"
                  contentStyle={popupContentStyle}
                  onOpen={(e) => { e.preventDefault(); }}
                  trigger={() => (
                    <a href="#" className="nav-v2-account-trigger" aria-label="Account menu">
                      <div className="nav-v2-account-avatar-wrap">
                        <img src={currentUser.avatar_thumb_url} className="nav-v2-account-avatar" />
                        <div className="nav-v2-account-status-dot" />
                      </div>
                      {isExpanded && (
                        <div className="nav-v2-account-info">
                          <div className="nav-v2-account-name">
                            {[currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ')}
                          </div>
                          <div className="nav-v2-account-email">{currentUser.email}</div>
                        </div>
                      )}
                    </a>
                  )}>
              {() => (
                <UserMenu csrfToken={csrfToken}
                          currentUser={currentUser}
                          team={team}
                          myTicketsLink={myTicketsLink}
                          teamMemberships={teamMemberships} />
              )}
            </Popup>
          </div>
        </div>
      </div>
    </div>
  );
};

AppMenuV2.propTypes = {
  navigationV2Menu: PropTypes.shape({
    anchors: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired,
    footer: PropTypes.array.isRequired,
  }).isRequired,
  team: PropTypes.object.isRequired,
  settingsLink: PropTypes.object,
  helpLink: PropTypes.object,
  myTicketsLink: PropTypes.object,
  isGuest: PropTypes.bool,
  csrfToken: PropTypes.string.isRequired,
  currentUser: PropTypes.object.isRequired,
  teamMemberships: PropTypes.array.isRequired,
  initialOpenGroups: PropTypes.arrayOf(PropTypes.string),
  initialClosedGroups: PropTypes.arrayOf(PropTypes.string),
  searchUrl: PropTypes.string,
  searchSections: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })),
};
AppMenuV2.defaultProps = { settingsLink: {}, helpLink: {}, myTicketsLink: {}, isGuest: false, initialOpenGroups: [], initialClosedGroups: [], searchUrl: null, searchSections: undefined };

export default AppMenuV2;
